<script setup lang="ts">
import {
  type Field,
  type FieldSource,
  type FacetConfig,
  SOURCE_META,
} from "../../../content/reports/ranked-explorer/fields";

const props = defineProps<{
  facet: FacetConfig;
  field: Field | undefined;
}>();

const emit = defineEmits<{
  (e: "update", patch: Partial<FacetConfig>): void;
  (e: "remove"): void;
}>();

const isOptions = computed(() => props.field?.kind === "options");
const sourceMeta = computed(() => SOURCE_META[(props.field?.source || "answer") as FieldSource]);

const editingLabel = ref(false);
const labelInput = ref(props.facet.label);

function startEdit() {
  labelInput.value = props.facet.label;
  editingLabel.value = true;
}

function commitLabel() {
  editingLabel.value = false;
  if (labelInput.value !== props.facet.label) {
    emit("update", { label: labelInput.value });
  }
}
</script>

<template>
  <div class="border rounded-lg bg-card">
    <!-- Header row -->
    <div class="flex items-start gap-2 px-2.5 py-2">
      <!-- Source dot -->
      <span
        class="flex size-[22px] items-center justify-center rounded-md shrink-0 mt-0.5"
        :style="{
          background: `color-mix(in oklch, ${sourceMeta.color}, transparent 86%)`,
          color: sourceMeta.color,
        }"
      >
        <Icon mode="svg" size="13" :name="sourceMeta.icon" />
      </span>

      <!-- Label + ref -->
      <div class="flex-1 min-w-0">
        <div v-if="editingLabel" class="flex">
          <input
            v-model="labelInput"
            autofocus
            class="h-[26px] w-full border rounded-md px-2 text-[13px] font-semibold bg-background outline-none focus:ring-1 focus:ring-ring"
            @blur="commitLabel"
            @keydown.enter="commitLabel"
            @keydown.escape="editingLabel = false"
          />
        </div>
        <button
          v-else
          class="inline-flex items-center gap-1.5 max-w-full bg-transparent border border-transparent rounded-md px-1 py-0.5 -mx-1 -my-0.5 cursor-text hover:bg-accent group"
          @click="startEdit"
        >
          <span class="text-[13px] font-semibold truncate">
            {{ facet.label || field?.label || facet.fieldId }}
          </span>
          <Icon mode="svg" size="11" name="lucide:pencil" class="text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
        </button>
        <div class="font-mono text-[10.5px] text-muted-foreground truncate mt-0.5">
          {{ field?.ref || facet.fieldId }}
        </div>
      </div>

      <!-- Remove -->
      <button
        class="size-[26px] inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer"
        title="Remove"
        @click="emit('remove')"
      >
        <Icon mode="svg" size="15" name="lucide:x" />
      </button>
    </div>

    <!-- Controls row -->
    <div class="flex items-center gap-2.5 px-2.5 pb-2.5 flex-wrap">
      <!-- Mode toggle -->
      <div class="inline-flex bg-muted rounded-md p-0.5 gap-0.5">
        <button
          class="h-7 px-2.5 inline-flex items-center gap-1.5 rounded text-xs font-medium transition-colors cursor-pointer"
          :class="facet.mode === 'options'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'"
          :disabled="!isOptions"
          :title="isOptions ? 'Filter by option list' : 'No options on this field'"
          @click="isOptions && emit('update', { mode: 'options' })"
        >
          <Icon mode="svg" size="13" name="lucide:list" />
          Options
        </button>
        <button
          class="h-7 px-2.5 inline-flex items-center gap-1.5 rounded text-xs font-medium transition-colors cursor-pointer"
          :class="facet.mode === 'text'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'"
          @click="emit('update', { mode: 'text' })"
        >
          <Icon mode="svg" size="13" name="lucide:search" />
          Text
        </button>
      </div>

      <!-- Toggles -->
      <div class="flex gap-3 ml-auto items-center">
        <label class="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground cursor-pointer whitespace-nowrap">
          <Switch
            :checked="facet.defaultOpen !== false"
            class="scale-75"
            @update:checked="(v: boolean) => emit('update', { defaultOpen: v })"
          />
          Open
        </label>
        <label class="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground cursor-pointer whitespace-nowrap">
          <Switch
            :checked="facet.allowNone !== false"
            class="scale-75"
            @update:checked="(v: boolean) => emit('update', { allowNone: v })"
          />
          "Not answered"
        </label>
      </div>
    </div>
  </div>
</template>
